import { layersWithPartialCustomTheme } from 'protomaps-themes-base'
import type { StyleSpecification } from 'maplibre-gl'
import { buildRasterStyle, rasterBasemap, type BasemapMode, type RasterBasemap } from './basemaps'

/**
 * The basemap style.
 *
 * Owning the tiles means owning the palette, and that is the whole point: protomaps'
 * stock dark theme is a fine general-purpose map, but here the map is a backdrop. Roads,
 * landcover and buildings are pulled toward the panel greys until the brightest thing on
 * screen is a truck. Labels stay legible -- place names are how an operator orients --
 * and nothing else competes.
 *
 * Glyphs and sprites are vendored under /tiles by `make fonts`, so a depot with no
 * internet still gets a fully rendered map. Devanagari ranges are included because half
 * the place names on this corridor are Hindi or Marathi and render as boxes without them.
 */

const SOURCE = 'basemap'

/**
 * Absolute, deliberately. Tile, glyph and sprite URLs are resolved inside MapLibre's
 * worker, whose base URL is the asset directory rather than the page -- a root-relative
 * "/tiles/..." silently becomes "/app/assets/tiles/..." and 404s with no error surfaced
 * anywhere. The map simply renders nothing.
 */
const origin = (): string => (typeof location === 'undefined' ? '' : location.origin)

/**
 * Partial theme overrides. Keys are protomaps' own theme slots, so this survives their
 * layer restructuring in a way that post-processing layer objects by id would not.
 */
const PALETTE = {
  // Land has to be visibly above the page background or the map reads as broken rather
  // than as dark. #141A20 against a #0F1418 body is a five-value difference: technically
  // rendering, indistinguishable from a blank canvas.
  background: '#07080F',
  earth: '#141926',
  water: '#0D1626',
  buildings: '#1E2433',

  park_a: '#1B2A22', park_b: '#1B2A22',
  wood_a: '#1B2A22', wood_b: '#1B2A22',
  scrub_a: '#1E2721', scrub_b: '#1E2721',
  industrial: '#222730', hospital: '#282029', school: '#22252E',
  pedestrian: '#1F2830', zoo: '#1B2A22', military: '#202429',
  sand: '#282619', beach: '#282619', glacier: '#20282F',
  aerodrome: '#20262E', runway: '#2C363E', pier: '#232E37',

  // Roads: readable as a corridor, never brighter than a marker.
  highway: '#3C4A60', major: '#334055', minor_a: '#2B3648', minor_b: '#2B3648',
  minor_service: '#252E3D', link: '#334055', other: '#222A38',
  highway_casing_early: '#1A2128', highway_casing_late: '#1A2128',
  major_casing_early: '#182026', major_casing_late: '#182026',
  minor_casing: '#161D23', minor_service_casing: '#161D23', link_casing: '#182026',
  railway: '#2E3840', boundaries: '#3A4650',

  tunnel_highway: '#2A343C', tunnel_major: '#262F36', tunnel_minor: '#222A31',
  tunnel_link: '#262F36', tunnel_other: '#1E262C',
  tunnel_highway_casing: '#151B21', tunnel_major_casing: '#151B21',
  tunnel_minor_casing: '#151B21', tunnel_link_casing: '#151B21',
  tunnel_other_casing: '#151B21',
  bridges_highway: '#3B4855', bridges_major: '#343F49', bridges_minor: '#2B353D',
  bridges_link: '#343F49', bridges_other: '#242C33',
  bridges_highway_casing: '#0F1418', bridges_major_casing: '#0F1418',
  bridges_minor_casing: '#0F1418', bridges_link_casing: '#0F1418',
  bridges_other_casing: '#0F1418',

  // Labels: the one place the basemap is allowed to be bright.
  city_label: '#A6B4C0', city_label_halo: '#0F1418',
  state_label: '#6C7B88', state_label_halo: '#0F1418',
  country_label: '#7E8D9A',
  subplace_label: '#8B9AA8', subplace_label_halo: '#0F1418',
  roads_label_major: '#6E7D8A', roads_label_major_halo: '#0F1418',
  roads_label_minor: '#5C6B78', roads_label_minor_halo: '#0F1418',
  ocean_label: '#3D5566', waterway_label: '#3D5566', peak_label: '#6C7B88',
  address_label: '#5C6B78', address_label_halo: '#0F1418',
}

/** What the map fits on load: the route bbox from routes.json, plus a little margin. */
export const CORRIDOR_BOUNDS: [number, number, number, number] = [74.75, 21.05, 75.55, 22.35]

/**
 * What the archive actually covers (Makefile TILE_BBOX). Used as maxBounds so panning
 * cannot wander off the extract -- past its edge there is simply no data, and an empty
 * grey field reads as a broken map rather than as the edge of a deliberate download.
 */
export const TILE_BOUNDS: [number, number, number, number] = [74.20, 20.75, 76.15, 22.65]

/**
 * Light palette for the command centre.
 *
 * The wall display sits in a lit operations room, where a dark map loses to reflections
 * and the fleet markers have to carry saturation the basemap cannot compete with. Land is
 * near-white, roads are pale ribbons, and every colour with meaning is spent on the
 * trucks -- the same rule as the dark theme, inverted.
 */
const LIGHT = {
  background: '#E9EEF5',
  earth: '#F7F9FC',
  water: '#DCE7F5',
  buildings: '#E6EBF2',

  park_a: '#E8F2E4', park_b: '#E8F2E4',
  wood_a: '#E4EFE0', wood_b: '#E4EFE0',
  scrub_a: '#EDF1E6', scrub_b: '#EDF1E6',
  industrial: '#EFEFF3', hospital: '#F5EBEF', school: '#F0F1F6',
  pedestrian: '#F0F2F6', zoo: '#E8F2E4', military: '#EEEFEC',
  sand: '#F5F0E2', beach: '#F5F0E2', glacier: '#EDF3F8',
  aerodrome: '#EDEFF4', runway: '#FFFFFF', pier: '#E6EBF2',

  highway: '#FFFFFF', major: '#FFFFFF', minor_a: '#FCFCFD', minor_b: '#FCFCFD',
  minor_service: '#FCFCFD', link: '#FFFFFF', other: '#FAFBFC',
  highway_casing_early: '#D7DEE9', highway_casing_late: '#D7DEE9',
  major_casing_early: '#DFE5EE', major_casing_late: '#DFE5EE',
  minor_casing: '#E7EBF1', minor_service_casing: '#E9EDF2', link_casing: '#DFE5EE',
  railway: '#CBD3E0', boundaries: '#C3CCDB',

  tunnel_highway: '#F4F6FA', tunnel_major: '#F4F6FA', tunnel_minor: '#F6F8FB',
  tunnel_link: '#F4F6FA', tunnel_other: '#F6F8FB',
  tunnel_highway_casing: '#E1E7EF', tunnel_major_casing: '#E1E7EF',
  tunnel_minor_casing: '#E7EBF1', tunnel_link_casing: '#E1E7EF',
  tunnel_other_casing: '#E7EBF1',
  bridges_highway: '#FFFFFF', bridges_major: '#FFFFFF', bridges_minor: '#FCFCFD',
  bridges_link: '#FFFFFF', bridges_other: '#FAFBFC',
  bridges_highway_casing: '#CDD6E3', bridges_major_casing: '#D5DCE7',
  bridges_minor_casing: '#E1E7EF', bridges_link_casing: '#D5DCE7',
  bridges_other_casing: '#E7EBF1',

  city_label: '#475569', city_label_halo: '#FFFFFF',
  state_label: '#94A3B8', state_label_halo: '#FFFFFF',
  country_label: '#8794A8',
  subplace_label: '#64748B', subplace_label_halo: '#FFFFFF',
  roads_label_major: '#8794A8', roads_label_major_halo: '#FFFFFF',
  roads_label_minor: '#9AA5B6', roads_label_minor_halo: '#FFFFFF',
  ocean_label: '#8CA6C4', waterway_label: '#8CA6C4', peak_label: '#94A3B8',
  address_label: '#9AA5B6', address_label_halo: '#FFFFFF',
}

export type MapTheme = 'dark' | 'light'

export function buildStyle(
  hasTiles: boolean, version = '', theme: MapTheme = 'dark', mode: BasemapMode = 'vector',
  // Which self-hosted archive to read: 'corridor' (Ultratech's Dhule/Jalgaon extract) or
  // 'mbmt' (Mira Bhayandar's own extract, added for MBMT's live map -- see MbmtLiveMap.tsx
  // and `make tiles-mbmt`). Same schema, same pipeline, different bbox.
  tileset = 'corridor',
  // Optional: a raster basemap (see basemaps.ts) painted as the TOPMOST layer, on top of
  // the vector style rather than replacing it -- unlike the `mode` raster short-circuit
  // above. MbmtLiveMap.tsx uses this to keep its original openstreetmap.org look for 2D
  // (the community raster tiles it always used) while 3D switches to the vector archive's
  // vector, buildings-3d layer requires. Toggling this layer's visibility is cheap; a live
  // setStyle() swap between two totally different styles is not (MapLibre does not
  // preserve custom sources/layers across it -- see MbmtLiveMap.tsx's own comment on why
  // it gates map construction on configLoaded instead of doing exactly that).
  rasterOverlay: RasterBasemap | null = null,
): StyleSpecification {
  // A remote raster basemap replaces the whole style rather than layering onto it: its
  // imagery already carries a palette, and the theme overrides below have nothing to
  // apply to. Photoreal is not handled here -- it is a deck.gl overlay above the vector
  // style, so it falls through and gets the vector basemap underneath it.
  const raster = rasterBasemap(mode)
  if (raster) return buildRasterStyle(raster)

  const light = theme === 'light'
  const bg = light ? LIGHT.background : '#07080F'

  const style: StyleSpecification = {
    version: 8,
    glyphs: `${origin()}/tiles/fonts/{fontstack}/{range}.pbf`,
    sprite: `${origin()}/tiles/sprites/${light ? 'light' : 'dark'}`,
    sources: {},
    layers: [{ id: 'bg', type: 'background', paint: { 'background-color': bg } }],
  }

  // No archive on disk: still a usable map. The geofence and route layers are added by
  // the layer modules regardless, so the operator sees the corridor on a plain field
  // rather than a blank screen with an error.
  if (!hasTiles) return style

  style.sources[SOURCE] = {
    type: 'vector',
    url: `pmtiles://${origin()}/tiles/v/${version || 'x'}/${tileset}.pmtiles`,
    attribution: '<a href="https://openstreetmap.org/copyright">© OpenStreetMap</a>',
  }
  style.layers = [
    ...style.layers,
    ...layersWithPartialCustomTheme(
      SOURCE, light ? 'light' : 'dark', light ? LIGHT : PALETTE, 'en', 'Devanagari'),
  ]

  // Real 3D buildings, not a photo-textured mesh: the archive's `buildings` layer already
  // carries `height`/`min_height` per feature (OSM's own building:levels/height tags,
  // via Planetiler's basemap profile) -- confirmed live by decoding the pmtiles archive
  // directly. fill-extrusion reads that for free, offline, with no API key, which is the
  // whole point of it existing beside the (billed, internet-only) Google Photoreal path.
  // Hidden by default and toggled by whichever view turns "3D" on -- see MbmtLiveMap.tsx.
  // 8m default height (missing on plenty of untagged OSM buildings) is roughly two storeys,
  // so an untagged building still reads as a building rather than as a paper-thin slab.
  //
  // INSERTED RIGHT BEFORE THE FIRST LABEL LAYER, NOT RIGHT AFTER THE FLAT `buildings`
  // FILL -- those are two very different positions. The flat `buildings` layer sits
  // EARLY (index ~30 of ~89), before every real road casing/fill/bridge layer (roads run
  // through index ~60): correct for 2D, where roads are meant to paint over building
  // footprints for legibility. But putting the extrusion at that same early spot meant
  // it (and, worse, anything inserted just before it, like MbmtLiveMap.tsx's route line)
  // sat UNDER every real road layer -- reported live as the route line and road
  // "overlapping/underlapping", because the road casing was painting back over the
  // route. fill-extrusion is depth-tested against whatever's drawn before it in the
  // stack, so it needs to sit AFTER roads (to actually occlude them from a tilted camera
  // when a building is genuinely in front) and BEFORE labels (so place/road names stay
  // legible on top of a building instead of vanishing behind one) -- the same position
  // Mapbox's own "3D buildings" example uses, for the same reason.
  const firstLabelIdx = style.layers.findIndex((l) => l.type === 'symbol')
  const extrusionIdx = firstLabelIdx >= 0 ? firstLabelIdx : style.layers.length
  const extrusion: StyleSpecification['layers'][number] = {
    id: 'buildings-3d',
    type: 'fill-extrusion',
    source: SOURCE,
    'source-layer': 'buildings',
    filter: ['in', 'kind', 'building', 'building_part'],
    layout: { visibility: 'none' },
    paint: {
      'fill-extrusion-color': light ? LIGHT.buildings : PALETTE.buildings,
      'fill-extrusion-height': ['coalesce', ['get', 'height'], 8],
      'fill-extrusion-base': ['coalesce', ['get', 'min_height'], 0],
      'fill-extrusion-opacity': 0.92,
    },
  }
  style.layers.splice(extrusionIdx, 0, extrusion)

  if (rasterOverlay) {
    // Last in the array, deliberately: layers paint back-to-front, so this has to sit
    // above every vector layer above (including buildings-3d) to actually hide them, not
    // just the background. Visible by default -- callers that also want 3D flip it off
    // once the map is up, matching the buildings-3d/buildings toggle pattern.
    style.sources['osm-raster'] = {
      type: 'raster', tiles: rasterOverlay.tiles, tileSize: 256,
      maxzoom: rasterOverlay.maxzoom, attribution: rasterOverlay.attribution,
    }
    style.layers.push({
      id: 'osm-raster', type: 'raster', source: 'osm-raster',
      layout: { visibility: 'visible' },
    })
  }

  return style
}
